<template>
  <div class="main-menu-mobile-drawer__wrapper">
    <transition name="drawer-fade">
      <div
        v-if="store.mobile.menuOpen"
        class="main-menu-mobile-drawer__overlay"
        aria-hidden="true"
        @click="close"
      />
    </transition>
    <transition name="drawer-slide">
      <div
        v-if="store.mobile.menuOpen"
        class="main-menu-mobile-drawer main-menu-mobile-drawer__panel"
        style="width: 300px"
      >
        <div class="main-menu-mobile-drawer__content">
          <div class="main-menu-mobile-drawer__header">
            <div class="main-menu-mobile-drawer__username-block">
              <span class="main-menu-mobile-drawer__username">
                {{ t('Hi, {0}', [store.user.firstName]) }}
              </span>
            </div>
            <button type="button" class="main-menu-mobile-drawer__close">
              <ui-icon
                class="main-menu-mobile-drawer__close-icon"
                size="12"
                :color="$colors.gray20"
                icon="fas fa-times"
                @click="close"
              />
            </button>
          </div>

          <menu-item
            v-if="isPaymentIssue"
            :title="t('Payment')"
            icon="fas fa-exclamation-circle"
            :warning="true"
            @click="handlePayment"
          ></menu-item>

          <menu-item
            :title="t('Home')"
            icon="fas fa-home"
            @click="handleReportsClick"
          ></menu-item>

          <menu-item
            :title="t('Events')"
            icon="fas fa-gauge"
            :dropdown="true"
            :color="isNavbarEventsRed ? $colors.rosa : $colors.white"
          >
            <navbar-events-menu :change-plan-click="close" />
          </menu-item>

          <menu-item
            v-if="reportingBoostViewEnabled"
            :title="t('Boost view')"
            icon="fas fa-rocket"
            :dropdown="true"
            :color="isNavbarEventsRed ? $colors.rosa : $colors.white"
          >
            <navbar-boost-view-menu :change-plan-click="close" />
          </menu-item>

          <menu-item
            :title="t('Settings')"
            icon="fas fa-cog"
            @click="handleSettingsClick"
          ></menu-item>

          <menu-item
            :title="t('Bookmarks')"
            icon="fas fa-star"
            @click="handleOpenBookmarkListDialog"
          ></menu-item>

          <menu-item
            v-model="supportDropdown"
            :dropdown="true"
            :title="t('Support')"
            icon="fas fa-question-circle"
          >
            <menu-item
              v-if="!isIntercomOpen"
              :title="t('Open live chat')"
              icon="fas fa-comment-alt"
              @click="handleOpenLiveSupportMobile"
            ></menu-item>

            <menu-item
              v-else
              :title="t('Close live chat')"
              icon="fas fa-times"
              @click="handleCloseLiveSupportMobile"
            ></menu-item>

            <menu-item
              :title="t('Help center')"
              icon="fas fa-book"
              @click="handleHelpCenterClick"
            ></menu-item>

            <menu-item
              :title="t('Blog')"
              icon="fas fa-th"
              @click="handleBlogClick"
            ></menu-item>

            <menu-item
              :title="t('Create temporary password')"
              icon="fas fa-ellipsis-h"
              @click="handleTemporaryPasswordClick"
            ></menu-item>

            <menu-item
              v-if="store.desktopViewForced === false"
              :title="t('Desktop view')"
              icon="fa fa-desktop"
              @click="handleMobileDesktopViewForced"
            ></menu-item>

            <menu-item
              v-if="store.desktopViewForced === true"
              :title="t('Mobile view')"
              icon="fa fa-mobile"
              @click="handleMobileDesktopViewForced"
            ></menu-item>
          </menu-item>

          <menu-item
            v-model="workspaceDropdown"
            :dropdown="true"
            :title="t('Workspaces')"
            icon="fas fa-window-restore"
          >
            <navbar-workspaces-menu
              v-if="store.mobile.menuOpen"
              :is-mobile="true"
              :close-menu="close"
            ></navbar-workspaces-menu>
          </menu-item>

          <div class="main-menu-mobile-drawer__list-item-spacer"></div>

          <menu-item
            :last="true"
            :title="t('Logout')"
            icon="fas fa-sign-out-alt"
            @click="handleLogout"
          ></menu-item>
        </div>
      </div>
    </transition>
    <bookmark-list-dialog ref="bookmarkListDialogRef" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useColors, useI18n, useLogout, useNavbarEvents, useNavbarSupport, usePaymentStatus, useUrl } from '@/composables';
import { useStore } from '@/store';
import config from '@/config';
import helpers from '@/services/helpers';
import $emitter from '@/services/emitter';
import MenuItem from '@/components/menu/menu-item.vue';
import BookmarkListDialog from '@/components/bookmark/bookmark-list-dialog.vue';
import NavbarWorkspacesMenu from '@/views/layout/navigation/navbar-workspaces-menu.vue';
import NavbarEventsMenu from '@/views/layout/navigation/navbar-events-menu.vue';
import NavbarBoostViewMenu from '@/views/layout/navigation/navbar-boost-view-menu.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const $colors = useColors();
const { defaultHomepage } = useUrl();
const paymentStatus = usePaymentStatus();
const navbarSupport = useNavbarSupport();
const { logout } = useLogout();

const supportDropdown = ref(false);
const workspaceDropdown = ref(false);
const bookmarkListDialogRef = ref<InstanceType<typeof BookmarkListDialog> | null>(null);

const isPaymentIssue = computed(() => paymentStatus.isPaymentIssue.value);
const helpCenterLink = computed(() => navbarSupport.helpCenterLink.value);
const isIntercomOpen = computed(() => navbarSupport.isIntercomOpen.value);
const reportingBoostViewEnabled = computed(
  () => store.user.featureFlags?.reportingBoostView?.enabled,
);

const { isNavbarEventsRed } = useNavbarEvents();

const registerOverlayListener = () => {
  const attachOverlayListener = () => {
    const overlay = document.querySelector('.main-menu-mobile-drawer__overlay');
    if (!overlay) {
      return false;
    }

    overlay.removeEventListener('click', close);
    overlay.addEventListener('click', close);
    return true;
  };

  helpers.afterBrowserRedraw(() => {
    if (!attachOverlayListener()) {
      setTimeout(() => {
        if (!attachOverlayListener()) {
          console.error("can't register overlay event listener");
        }
      }, 700);
    }
  });
};

watch(
  () => store.mobile.menuOpen,
  value => {
    if (!value) {
      return;
    }
    supportDropdown.value = window.innerHeight > 500;
    registerOverlayListener();
  },
);

const close = () => {
  store.setMenuOpen(false);
};

const handleMobileDesktopViewForced = () => {
  navbarSupport.toggleMobileDesktopViewForced();
  close();
};

const handleOpenBookmarkListDialog = () => {
  bookmarkListDialogRef.value?.open();
  close();
};

const handleOpenLiveSupportMobile = () => {
  navbarSupport.openLiveSupport();
  close();
};

const handleCloseLiveSupportMobile = () => {
  navbarSupport.closeLiveSupport();
  close();
};

const handleHelpCenterClick = () => {
  window.open(helpCenterLink.value, '_blank');
};

const handleBlogClick = () => {
  window.open(config.blogLink, '_blank');
};

const handleTemporaryPasswordClick = () => {
  $emitter.emit('open-dialog', { name: 'TemporaryPasswordDialog' });
  close();
};

const handleReportsClick = () => {
  router.push(defaultHomepage());
  close();
};

const handleSettingsClick = () => {
  router.push({ name: 'app.profile' });
  close();
};

const handleLogout = () => {
  logout({
    beforeNavigate: () => close(),
  });
};

const handlePayment = () => {
  router.push('/app/profile/subscription');
  close();
};
</script>

<style lang="scss">
.main-menu-mobile-drawer__overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1001;
}
.main-menu-mobile-drawer__panel {
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  z-index: 1002;
  border: none !important;
  overflow: auto;
}
.drawer-fade-enter-active,
.drawer-fade-leave-active {
  transition: opacity 0.2s ease;
}
.drawer-fade-enter-from,
.drawer-fade-leave-to {
  opacity: 0;
}
.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.25s ease;
}
.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(-100%);
}

.main-menu-mobile-drawer {
  z-index: 1001;
  border: none !important;

  &__content {
    display: flex;
    flex-direction: column;
    background-color: var(--color-bluedeep);
    min-height: 100%;
  }

  &__header {
    display: flex;
    flex-direction: row;
    min-height: 40px;
    border-bottom: 1px solid var(--color-mobile-menu-border);
    color: var(--color-gray20);
    flex-grow: 0;
    height: 54px;
    user-select: auto;
  }

  &__username-block {
    display: flex;
    flex-grow: 1;
    flex-wrap: nowrap;
    overflow: hidden;
    align-items: center;
    border-right: 1px solid var(--color-mobile-menu-border);
    padding: 16px;
  }

  &__username {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 18px;
    color: var(--color-white);
  }

  &__close {
    display: flex;
    flex-grow: 0;
    width: 45px;
    min-width: 45px;
    justify-content: center;
    align-items: center;
  }

  &__list-item-spacer {
    flex-grow: 1;
  }

  &__bookmark-list-activator {
    width: 100%;
  }

  &__bookmark-list {
    width: 100%;

    .v-menu,
    .v-menu__activator {
      width: 100%;
    }
  }
  .menu-list-item__dropdown-body {
    background: var(--color-graphite);
  }
}
</style>
